import type { LabType } from "../types/lab";
import { type ApiResponse, authApi, type GenericErrorResponse } from "./authService";

export interface ChecklistQuestion {
  id: number;
  question: string;
  lab_type: LabType;
  level: ChecklistLevel;
  for_auditor: boolean;
  suggestion?: string;
}

export type ChecklistScope = 'lab' | 'auditor';
export type ChecklistLevel = 'bronze' | 'silver' | 'gold';
type Answer = 'y' | 'n' | 'x';

export interface ChecklistInstance {
  id: number;
  certification_id: number;
  scope: ChecklistScope;
  revision: number;
  status: 'draft' | 'submitted' | 'archived';
  reviewed_lab_instance_id: number | null;
  compared_against_instance_id: number | null;
  created_at: string | null;
  created_by: string;
  submitted_at: string | null;
  submitted_by: string | null;
  comments: string | null;
  archived_at: string | null;
  archived_by_instance_id: number | null;
}

export interface ChecklistInstanceAnswer {
  checklist_instance_id: number;
  question_id: number;
  answer: Answer | null;
  na_reason?: number | null;
  na_comment?: string | null;
  updated_by: string | null;
  updated_at: string | null;
}

export interface ChecklistInstanceResponse extends ApiResponse {
  certification_id: number;
  lab_id: string;
  lab_type: LabType;
  certification_level: ChecklistLevel;
  certification_state: string;
  instance: ChecklistInstance;
  questions: ChecklistQuestion[];
  answers: ChecklistInstanceAnswer[];
  read_only: boolean;
  has_substantive_changes: boolean;
  requires_checklist_changes_before_relock: boolean;
}

interface ChecklistHistoryResponse extends ApiResponse {
  history: ChecklistInstance[];
}

interface ChecklistAnswerUpdateResponse extends ApiResponse {
  answer: ChecklistInstanceAnswer;
}

interface ChecklistInstanceSubmitResponse extends ApiResponse {
  certification_id: number;
  instance_id: number;
  status: ChecklistInstance['status'];
}

export class ChecklistService {
  static async getCurrentChecklist(
    certificationId: number,
    scope: ChecklistScope = 'lab',
  ): Promise<ChecklistInstanceResponse> {
    const response = await authApi(
      `/certifications/${certificationId}/checklists/current?scope=${scope}`
    ) as ChecklistInstanceResponse | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error);
    }

    return response;
  }

  static async getChecklistInstance(
    certificationId: number,
    instanceId: number,
  ): Promise<ChecklistInstanceResponse> {
    const response = await authApi(
      `/certifications/${certificationId}/checklists/${instanceId}`
    ) as ChecklistInstanceResponse | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error);
    }

    return response;
  }

  static async getChecklistHistory(
    certificationId: number,
    scope: ChecklistScope | 'all' = 'all',
  ): Promise<ChecklistHistoryResponse> {
    const response = await authApi(
      `/certifications/${certificationId}/checklists/history?scope=${scope}`
    ) as ChecklistHistoryResponse | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error);
    }

    return response;
  }

  static async saveChecklistInstanceAnswer(
    certificationId: number,
    instanceId: number,
    questionId: number,
    answer: Answer,
    naReason?: number,
    naComment?: string,
  ): Promise<ChecklistAnswerUpdateResponse> {
    const response = await authApi(
      `/certifications/${certificationId}/checklists/${instanceId}/answers/${questionId}`,
      {
        method: 'PUT',
        body: JSON.stringify({
          answer,
          na_reason: naReason,
          na_comment: naComment,
        }),
      }
    ) as ChecklistAnswerUpdateResponse | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error);
    }

    return response;
  }

  static async submitChecklistInstance(
    certificationId: number,
    instanceId: number,
    comments?: string,
  ): Promise<ChecklistInstanceSubmitResponse> {
    const response = await authApi(
      `/certifications/${certificationId}/checklists/${instanceId}/submit`,
      {
        method: 'POST',
        body: JSON.stringify({ comments }),
      }
    ) as ChecklistInstanceSubmitResponse | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error);
    }

    return response;
  }

  static async requestFurtherReview(
    certificationId: number,
    comments: string,
  ): Promise<ApiResponse & { certification_id: number; state: string }> {
    const response = await authApi(
      `/certifications/${certificationId}/review/request`,
      {
        method: 'POST',
        body: JSON.stringify({ comments }),
      }
    ) as (ApiResponse & { certification_id: number; state: string }) | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error);
    }

    return response;
  }
}
